import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import _, { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../components/ui/Button';

import { useGetPlaylistLayerContent, useUpdateCampaignContentSettings, useUpdateMedia, useUpdatePlaylistContentSettings } from '../../../queries';
import { handleRssSetting, trimFileNameExc } from '../../../config/global';
import { readImageFit } from '../../../utils/imageFit';
import { TYPES_ALLOWED_STRETCH } from '../../../Constants/media';
import { setRssSettings } from '../../../redux/Slices/Apps/appsSlice';
import { toggleDialog } from '../../../redux/Slices/Global/globalSlice';
import RssSettings from '../RSS/RssSettings';
import { Media } from '../ShowMedia/Media';
import LazyImage from '../ShowMedia/LazyImage';
import { ProgressSpinner } from 'primereact/progressspinner';
import CornerMediaPreview from './components/CornerMediaPreview';
import CornerAreaControl from './components/CornerAreaControl';

const defaultArea = {
    start_margin: 0,
    top_margin: 0,
    width: 35,
    height: 37,
    layout: "landscape-wide"
};


const rssTypes = ["RSS", "RSS_CUSTOM"]

function PlaylistSettingPopup({ item, removeMedia, setSelectedRow }) {
    const { t } = useTranslation();
    const { formState: { errors }, control, reset } = useForm();
    const { layer, openDialog } = useSelector((state) => state.global);

    const { rssSettings } = useSelector(state => state.apps);
    const dispatch = useDispatch();
    // ⚠️ MUST be the layer this popup was opened for, not a literal "MAIN".
    // GET /playlist/{id}/{layer}/media filters playlist_media.type = upper(layer).
    // Fast create writes music as type='MAIN' but photos as type='PLAYGROUND'
    // (CafeWizardService::attachPlaylistLayer), so a hardcoded "MAIN" matched the
    // music playlist and returned ZERO rows for the photos one — the overlay
    // playlist rendered EMPTY while playing correctly on screen. The rows were
    // never missing: they exist in BOTH playlist_media and published_playlist_media.
    // `layer` is the same value this component already switches on below.
    const { isLoading: fetchingPlaylistContent, data, refetch: refetchLayerData } = useGetPlaylistLayerContent(item.playlist_id, layer)

    const updateCampaignItemSetting = useUpdateCampaignContentSettings();
    const updatePlaylistItemSetting = useUpdatePlaylistContentSettings();
    const updateMediaName = useUpdateMedia();
    const loading = useMemo(() => updateCampaignItemSetting?.isLoading || updatePlaylistItemSetting.isLoading,
        [updateCampaignItemSetting?.isLoading, updatePlaylistItemSetting.isLoading])

    const [editName, setEditName] = useState(false)
    const [name, setName] = useState(trimFileNameExc(item.file_name))
    const [setting, setSetting] = useState({
        "show_video_screen": !!item.show_video_screen,
        "is_muted": !!item.is_muted,
        "maintain_aspect_ratio": !!item.maintain_aspect_ratio,
        "image_fit": readImageFit(item),
        "is_showed": !!item.is_showed,
        "settings": item.media_settings,
        "downloadable": !!item.downloadable,
        "show_layer_two": !!item.show_layer_two,
        "show_layer_one": !!item.show_layer_one,
        "show_layer_three": !!item.show_layer_three,
    });
    const [cornerMediaSettings, setCornerMediaSettings] = useState(item.media_settings)


    const updateSetting = async () => {
        const formData = new FormData();

        for (var key in setting) {
            if (key === "settings") {
                const orderedSettings = {
                    width: cornerMediaSettings?.width || 100,
                    height: cornerMediaSettings?.height || 100,
                    top_margin: cornerMediaSettings?.top_margin || 0,
                    start_margin: cornerMediaSettings?.start_margin || 0,
                };
                formData.append("settings", JSON.stringify(orderedSettings));
            } else if (key === "image_fit") {
                // Number('crop') is NaN — this key is a string, not a flag.
                if (setting.image_fit) formData.append("image_fit", setting.image_fit);
            } else {
                formData.append(key, Number(setting[key]));
            }
        }

        await updateCampaignItemSetting.mutateAsync({ data: formData, id: item.campaign_playlist_media_id })
        setSelectedRow({})

    }



    //Dialog Header 
    const header = () => {
        return (
            <h4 className='m-0 fw-bold d-flex align-items-center'>
                <span>{t('dragdrop.playlist_label')}: {item.playlist_title}</span>
            </h4>
        );
    }

    const onclose = () => {
        dispatch(toggleDialog("playlistSettingPopup"))
        setSelectedRow({})
    }



    const CardContent = ({ item }) => {
        return <>
            <div className={` col-5 p-2 me-2`} >
                {

                    <div className="w-100   d-flex align-items-center justify-content-center h-[60px] overflow-hidden">
                        {/* <Media data={item} /> */}
                        <LazyImage
                            media={item}
                            popupType="playlist_media_setting"
                            index={item.id}
                            styleClass="me-2 image_container max-w-full max-h-full w-auto h-auto object-contain"
                        />
                    </div>

                }
            </div>
            <div className={` col-7   text-dark `} >
                <div>{item.type.toLowerCase()} : {item.file_name.length > 25 ? item.file_name.slice(0, 25) + "..." : item.file_name}</div>
                <div className={` fs-8 fw-light  text-truncate `} >{t('dragdrop.duration')}: {item.custom_duration} </div>
            </div>

        </>
    }


    return (
        <>
            <Dialog visible={openDialog.playlistSettingPopup}
                onHide={onclose} draggable={false}
                breakpoints={{ '960px': '95vw' }}
                style={{ width: '50vw', minHeight: '300px' }}
                header={header} >
                <div className="d-flex">
                    {
                        layer === "CORNER" && (item.type == "IMAGE" || item.type == "CORNER") ?
                            <CornerMediaPreview imageFit={setting.image_fit} maintainAspectRatio={setting.maintain_aspect_ratio} item={""} area={cornerMediaSettings} setArea={setCornerMediaSettings} />

                            :

                            <div className="col-6 col-lg-7 border-end d-flex flex-column justify-content-start align-content-center flex-wrap p-3 hight">
                                <div className={`col-12 d-flex flex-column   overflow-y-auto scroll_div`} style={{ maxHeight: "400px" }}>
                                    {
                                        fetchingPlaylistContent ?
                                            <div className='w-100 h-100 d-flex align-items-center' >
                                                <ProgressSpinner style={{ width: '60px', height: '60px', }} strokeWidth="3" fill="var(--surface-ground)" />
                                            </div>
                                            :


                                            data?.length != 0 ?

                                                data?.map((item, index) => (
                                                    <div key={index} className="overflow-hidden col-12 d-flex align-items-start p-1 mb-1 rounded bg-label-secondary border">
                                                        <CardContent item={item} />
                                                    </div>
                                                ))
                                                : ""
                                    }
                                </div>
                            </div>
                    }



                    <div className="col-6 col-lg-5  px-0 d-flex flex-column justify-content-between overflow-y-auto">


                        <div className="px-2 mt-3">
                            <h6 className='text-center text-black fw-bold'>{t('dragdrop.media_settings')}</h6>
                            <div className="d-flex justify-content-between mb-2">
                                <span className='fs-8'>{t('dragdrop.show_video_screen')}</span>
                                <InputSwitch checked={setting.show_video_screen} onChange={(e) => setSetting({ ...setting, "show_video_screen": e.value })} />
                            </div>

                            <div className="d-flex justify-content-between mb-2">
                                <span className='fs-8'>{t('dragdrop.showed')}</span>
                                <InputSwitch checked={setting.is_showed} onChange={(e) => setSetting({ ...setting, "is_showed": e.value })} />
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span className='fs-8'>{t('dragdrop.mute')}</span>
                                <InputSwitch checked={setting.is_muted} onChange={(e) => setSetting({ ...setting, "is_muted": e.value })} />
                            </div>
                            {/* Same three modes as MediaSettingPopup — see the note
                                there on why the old two-state toggle was wrong. */}
                            <div className="mb-2">
                                <span className='fs-8 d-block mb-1'>{t('dragdrop.image_fit.label')}</span>
                                <div className="d-flex gap-1 flex-wrap">
                                    {['fit', 'crop', 'fill'].map((mode) => {
                                        const active = (setting.image_fit || (setting.maintain_aspect_ratio ? 'fit' : 'fill')) === mode;
                                        return (
                                            <button
                                                key={mode} type="button"
                                                disabled={!(TYPES_ALLOWED_STRETCH.includes(item.type))}
                                                onClick={() => setSetting((prev) => ({
                                                    ...prev,
                                                    image_fit: mode,
                                                    maintain_aspect_ratio: mode !== 'fill',
                                                }))}
                                                className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                style={{ fontSize: '0.72rem', flex: '1 1 0', minWidth: 0 }}
                                                title={t(`dragdrop.image_fit.${mode}_hint`)}
                                            >
                                                {t(`dragdrop.image_fit.${mode}`)}
                                            </button>
                                        );
                                    })}
                                </div>
                                <small className="text-muted d-block mt-1" style={{ fontSize: '0.68rem' }}>
                                    {t(`dragdrop.image_fit.${setting.image_fit || (setting.maintain_aspect_ratio ? 'fit' : 'fill')}_hint`)}
                                </small>
                            </div>

                            {layer === "CORNER" && (item.type == "IMAGE" || item.type == "CORNER") ? (
                                <div className="px-2 border-top py-3" >
                                    <h6 className='text-center text-black fw-bold'>{t('dragdrop.corner_settings')}</h6>
                                    <CornerAreaControl area={cornerMediaSettings} setArea={setCornerMediaSettings} />
                                </div>
                            ) : " "}

                        </div>


                        {
                            layer == "MAIN" && window.location.pathname.includes("campaign") ?
                                <div className="px-2 border-top py-3" >
                                    <h6 className='text-center text-black fw-bold'>{t('dragdrop.play_settings')}</h6>

                                    <div className="d-flex justify-content-between mb-2">
                                        <span className='fs-8'>{t('dragdrop.show_playground_layer')}</span>

                                        <InputSwitch checked={setting.show_layer_one} onChange={(e) => setSetting({ ...setting, "show_layer_one": e.value })} />
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className='fs-8'>{t('dragdrop.show_corner_layer')}</span>
                                        <InputSwitch checked={setting.show_layer_two} onChange={(e) => setSetting({ ...setting, "show_layer_two": e.value })} />
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className='fs-8'>{t('dragdrop.show_banner_layer')}</span>
                                        <InputSwitch inputId=' `${ item.id }-layer-three` }' checked={setting.show_layer_three} onChange={(e) => setSetting({ ...setting, "show_layer_three": e.value })} />
                                    </div>

                                </div>
                                :
                                ""
                        }



                        <div className='border-top px-2 py-3 d-flex justify-content-between align-items-center'>

                            <Button label={["RSS", "RSS_CUSTOM"].includes(item.type) ? t('buttons.publish') : t('buttons.update')}
                                loading={loading}
                                className='btn btn-primary fs-8 py-1 px-2'
                                onClick={updateSetting} />
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    )
}

export default PlaylistSettingPopup